<script lang="ts">
  import type { FieldProps } from './Field.types';

  export * from './Field.types';
</script>

<script setup lang="ts">
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, useAttrs, useSlots } from 'vue';

  import Label from '../Label/Label.vue';

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<FieldProps>(), {
    addBottomSpace: false,
    errorText: undefined,
    hintText: undefined,
    id: undefined,
    errorId: undefined,
    isRequired: false,
    label: undefined,
    showOptionalInLabel: false,
    isLabelSrOnly: false,
    fieldset: false,
    isDisabled: false,
    disabled: false,
  });
  const attrs = useAttrs();
  const slots = useSlots();
  const fieldId = computed(() => props.id || uniqueId('stash-field-'));
  const fieldErrorId = computed(() => props.errorId || uniqueId('stash-field-error-'));
  const labelId = computed(() => uniqueId('stash-field-label-'));
  const hasError = computed(() => !!props.errorText);
  const wrapperElement = computed(() => (props.fieldset ? 'fieldset' : 'div'));

  // Any attributes that are unique to form elements, you want to exclude from
  // being bound from the root element.
  const rootAttrs = computed(() => {
    const { placeholder, ...otherAttrs } = attrs;

    return otherAttrs;
  });
</script>

<template>
  <component
    :is="wrapperElement"
    data-test="stash-field"
    class="stash-field"
    :class="[
      { 'p-0': props.fieldset },
      { 'mb-9': props.addBottomSpace && !props.errorText && !props.hintText && !slots.hint },
      { 'mb-4': props.addBottomSpace && (props.errorText || props.hintText || slots.hint) },
      { 'stash-field--disabled': props.isDisabled || props.disabled },
      { 'stash-field--is-read-only': props.isReadOnly },
      { 'stash-field--is-required': props.isRequired },
      { 'stash-field--has-error': hasError },
    ]"
    v-bind="rootAttrs"
  >
    <Label
      v-if="props.label"
      :id="labelId"
      :for="fieldId"
      :has-error="hasError"
      :is-required="isRequired"
      :show-optional="props.showOptionalInLabel"
      :is-sr-only="props.isLabelSrOnly"
      :legend="props.fieldset"
      :disabled="props.isDisabled || props.disabled"
    >
      {{ props.label }}
    </Label>

    <!-- @slot for the form field; the Label can also be rendered here instead of using the label prop -->
    <slot
      :field-id="fieldId"
      :field-error-id="fieldErrorId"
      :has-error="hasError"
      :is-required="isRequired"
      :is-read-only="props.isReadOnly"
      :disabled="props.isDisabled || props.disabled"
      :label-id="labelId"
      :show-optional-in-label="props.showOptionalInLabel"
    ></slot>

    <span
      v-if="props.errorText"
      :id="fieldErrorId"
      class="stash-field-error mt-1 block whitespace-pre-line text-xs text-red-500"
      data-test="stash-field-error"
    >
      {{ props.errorText }}
    </span>

    <span
      v-else-if="props.hintText && !props.isReadOnly"
      class="stash-field-hint mt-1 block whitespace-pre-line text-xs"
      data-test="stash-field-hint"
    >
      {{ props.hintText }}
    </span>

    <div
      v-else-if="slots.hint && !props.isReadOnly"
      class="stash-field-hint mt-1 whitespace-pre-line text-xs"
      data-test="stash-field-hint"
    >
      <!-- @slot for displaying hint text below the field -->
      <slot name="hint"></slot>
    </div>
  </component>
</template>
